/**
 * ImageCarousel Component
 * 
 * Auto-rotating image carousel with glassy navigation buttons.
 * Used in the feature showcase section.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Section 1 images (AANA Workspace)
import s1Img1 from './assets/section1/1.s1.webp';
import s1Img2 from './assets/section1/1.s2.webp';
import s1Img3 from './assets/section1/1.s3.webp';
import s1Img4 from './assets/section1/1.s4.webp';
import s1Img5 from './assets/section1/1.s5.webp';

const SECTION1_IMAGES = [
  { src: s1Img1, alt: 'AANA Workspace - Visual generation from content' },
  { src: s1Img2, alt: 'AANA Workspace - Diagram overlay on materials' },
  { src: s1Img3, alt: 'AANA Workspace - Annotation tools in action' },
  { src: s1Img4, alt: 'AANA Workspace - Multi-format content support' },
  { src: s1Img5, alt: 'AANA Workspace - Real-time collaboration' },
];

const AUTO_ROTATE_INTERVAL = 5000; // 5 seconds

// Animation variants
const slideVariants = {
  enter: (direction) => ({
    opacity: 0,
    scale: 0.95,
    x: direction > 0 ? 50 : -50,
  }),
  center: {
    opacity: 1,
    scale: 1,
    x: 0,
    zIndex: 1,
  },
  exit: (direction) => ({
    opacity: 0,
    scale: 0.95,
    x: direction < 0 ? 50 : -50,
    zIndex: 0,
  }),
};

const ImageCarousel = ({ images = SECTION1_IMAGES, className = '' }) => {
  const [[currentIndex, direction], setSlide] = useState([0, 0]);
  const [isPaused, setIsPaused] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  const slideCount = images.length;

  // Pause timer when offscreen
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Navigate to next slide
  const goToNext = useCallback(() => {
    setSlide(([prev]) => [(prev + 1) % slideCount, 1]);
  }, [slideCount]);

  // Navigate to previous slide
  const goToPrev = useCallback(() => {
    setSlide(([prev]) => [(prev - 1 + slideCount) % slideCount, -1]);
  }, [slideCount]);

  // Go to specific slide
  const goToSlide = useCallback((index) => {
    setSlide(([prev]) => [index, index > prev ? 1 : -1]);
  }, []);

  // Auto-rotation - pauses when offscreen or hovered
  useEffect(() => {
    if (isPaused || !isVisible) return;

    const timer = setInterval(goToNext, AUTO_ROTATE_INTERVAL);
    return () => clearInterval(timer);
  }, [goToNext, isPaused, isVisible]);

  // Pause on hover (desktop)
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  return (
    <div
      ref={containerRef}
      className={`image-carousel ${className}`.trim()}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Image container */}
      <div className="image-carousel__viewport">
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.img
            key={currentIndex}
            src={images[currentIndex].src}
            alt={images[currentIndex].alt}
            className="image-carousel__image"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              opacity: { duration: 0.4 },
              scale: { duration: 0.4 },
              x: { type: 'spring', stiffness: 300, damping: 30 },
            }}
            draggable={false}
          />
        </AnimatePresence>
      </div>

      {/* Navigation buttons - inline styles for guaranteed sizing */}
      <button
        onClick={goToPrev}
        aria-label="Previous slide"
        style={{
          position: 'absolute',
          left: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.95)',
          background: 'rgba(51, 92, 255, 0.4)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(51, 92, 255, 0.2)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>‹</span>
      </button>

      <button
        onClick={goToNext}
        aria-label="Next slide"
        style={{
          position: 'absolute',
          right: '12px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.95)',
          background: 'rgba(51, 92, 255, 0.4)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(51, 92, 255, 0.2)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>›</span>
      </button>

      {/* Slide indicators */}
      <div className="image-carousel__indicators">
        {images.map((_, index) => (
          <button
            key={index}
            className={`image-carousel__indicator ${index === currentIndex ? 'image-carousel__indicator--active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === currentIndex ? 'true' : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageCarousel;
